import React, { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import type { CalendarDay, CalendarTrade } from './calendarUtils';
import { computeDayTradeSummary } from './calendarUtils';
import styles from './DayDetail.module.css';

interface DayDetailProps {
  day: CalendarDay;
  onClose: () => void;
  onReplayTrade?: (decisionSK: string) => void;
}

interface TradeRowProps {
  trade: CalendarTrade;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onReplayTrade?: (decisionSK: string) => void;
}

function formatCurrency(value: number): string {
  const sign = value >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(value).toFixed(2)}`;
}

const TradeRow: React.FC<TradeRowProps> = ({ trade, index, expanded, onToggle, onReplayTrade }) => {
  const pnlClass = trade.pnl >= 0 ? styles.pnlPositive : styles.pnlNegative;
  const pnlPctClass = trade.pnlPct >= 0 ? styles.pnlPositive : styles.pnlNegative;
  const directionClass = trade.direction.toLowerCase() === 'long' ? styles.directionLong : styles.directionShort;
  const pnlPctPrefix = trade.pnlPct >= 0 ? '+' : '';

  return (
    <div className={styles.tradeRow}>
      <button
        type="button"
        data-testid="trade-row"
        className={`${styles.tradeGrid} ${styles.tradeButton}`}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span data-testid="trade-symbol" className={styles.symbol}>
          {trade.symbol}
        </span>
        <span data-testid="trade-direction" className={directionClass}>
          {trade.direction.toLowerCase() === 'long' ? 'LONG' : 'SHORT'}
        </span>
        <span className={styles.price}>{trade.entryPrice.toFixed(2)}</span>
        <span className={styles.price}>{trade.exitPrice.toFixed(2)}</span>
        <span data-testid="trade-pnl" className={pnlClass}>
          {formatCurrency(trade.pnl)}
        </span>
        <span data-testid="trade-pnl-pct" className={pnlPctClass}>
          {pnlPctPrefix}
          {trade.pnlPct.toFixed(1)}%
        </span>
        <span className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`}>&#9660;</span>
      </button>
      {expanded && (
        <div data-testid={`reasoning-card-${index}`} className={styles.reasoningCard}>
          <p>{trade.reasoning}</p>
          {trade.decisionSK && onReplayTrade && (
            <button type="button" className={styles.replayButton} onClick={() => onReplayTrade(trade.decisionSK!)}>
              Replay decision
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const DayDetail: React.FC<DayDetailProps> = ({ day, onClose, onReplayTrade }) => {
  const [expandedTrades, setExpandedTrades] = useState<Set<number>>(new Set());

  const sortedTrades = useMemo(
    () => [...day.trades].sort((a, b) => b.closedAt.localeCompare(a.closedAt)),
    [day.trades],
  );
  const summary = useMemo(() => computeDayTradeSummary(day.trades), [day.trades]);
  const allExpanded = sortedTrades.length > 0 && expandedTrades.size === sortedTrades.length;

  const toggleTrade = (index: number) => {
    setExpandedTrades((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAllTrades = () => {
    setExpandedTrades(allExpanded ? new Set() : new Set(sortedTrades.map((_, index) => index)));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{format(parseISO(day.date), 'EEEE, MMMM d, yyyy')}</h3>
        <div className={styles.headerActions}>
          <button className={styles.secondaryButton} onClick={toggleAllTrades} disabled={sortedTrades.length === 0}>
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
      </div>
      <div className={styles.summaryGrid} aria-label="Day trade summary">
        <div>
          <span>Total P&L</span>
          <strong className={summary.totalPnl >= 0 ? styles.pnlPositive : styles.pnlNegative}>
            {formatCurrency(summary.totalPnl)}
          </strong>
        </div>
        <div>
          <span>Win Rate</span>
          <strong>{summary.winRate}%</strong>
        </div>
        <div>
          <span>Best Trade</span>
          <strong>
            {summary.bestTrade ? `${summary.bestTrade.symbol} ${formatCurrency(summary.bestTrade.pnl)}` : '—'}
          </strong>
        </div>
        <div>
          <span>Worst Trade</span>
          <strong>
            {summary.worstTrade ? `${summary.worstTrade.symbol} ${formatCurrency(summary.worstTrade.pnl)}` : '—'}
          </strong>
        </div>
        <div>
          <span>Main Strategy</span>
          <strong>{summary.topStrategy || '—'}</strong>
        </div>
      </div>
      <div className={styles.headerRow}>
        <span className={styles.headerLabel}>Symbol</span>
        <span className={styles.headerLabel}>Dir</span>
        <span className={styles.headerLabelRight}>Entry</span>
        <span className={styles.headerLabelRight}>Exit</span>
        <span className={styles.headerLabelRight}>P&L</span>
        <span className={styles.headerLabelRight}>P&L%</span>
        <span className={styles.headerLabel}></span>
      </div>
      <div className={styles.tradeList}>
        {sortedTrades.map((trade, i) => (
          <TradeRow
            key={i}
            trade={trade}
            index={i}
            expanded={expandedTrades.has(i)}
            onToggle={() => toggleTrade(i)}
            onReplayTrade={onReplayTrade}
          />
        ))}
      </div>
    </div>
  );
};

export default DayDetail;
